/**
 * ============================================================
 * AdminUsers.js — Manage Admin Accounts
 * ============================================================
 *
 * Loads:
 *   - List of admin users (GET /api/admin/users)
 * 
 * Features:
 *   - Click a row to edit/delete an admin user
 *   - "Create Admin User" button -> /admin/users/new
 * 
 *  Navigation:
 *   - Uses SynlabAdminNavbar for consistent admin navigation
 *   - Uses shared AdminSubpage.css for layout
 **/

import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";

import SynlabAdminNavbar from "../components/SynlabAdminNavbar";
import SynlabAdminFooter from "../components/SynlabAdminFooter";

import { authFetch } from "../../utils/authFetch";

import "./css/AdminSubpage.css";

// ------------------------------------------------------------
// Component
// ------------------------------------------------------------
function AdminUsers() {

  const [users, setUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);

  const navigate = useNavigate();

  // ------------------------------------------------------------
  // Validate token on load + load logged-in user
  // ------------------------------------------------------------
  useEffect(() => {
    async function validateToken() {
      try {
        const res = await authFetch("/api/authenticate");
        if (!res) return;

        // Decode token payload to get the user id
        const token = localStorage.getItem("token");
        if (token) {
          const payload = JSON.parse(atob(token.split(".")[1]));
          setCurrentUser(payload);
        }

      } catch (err) {
        console.error("Token validation failed:", err);
      }
    }

    validateToken();
  }, []);

  // ------------------------------------------------------------
  // Load all admin users after validation
  // ------------------------------------------------------------
  useEffect(() => {
    async function loadUsers() {
      try {
        const res = await authFetch("/api/admin/users");
        if (!res) return;

        setUsers(await res.json());

      } catch (err) {
        console.error("Failed to load admin users:", err);
      }
    }

    loadUsers();
  }, []);

  // ------------------------------------------------------------
  // Row click navigation
  // ------------------------------------------------------------
  function handleRowClick(user) {
    navigate(`/admin/users/edit/${user.id}`);
  }

  // ----------------------------------------------------------
  // Render
  // ----------------------------------------------------------
  return (
    <div className="admin-wrapper">

      <SynlabAdminNavbar />

      <div className="admin-subpage-container">

        {/* ----- PAGE TITLE + CREATE BUTTON ----- */}
        <div className="admin-page-header">
          <h1 className="admin-page-title">Manage Admin Accounts</h1>

          <button
            className="btn btn-primary admin-add-button"
            onClick={() => navigate("/admin/users/new")}
          >
            + Create Admin User
          </button>
        </div>

        {/* ----- PAGE DESCRIPTION ----- */}
        <p className="admin-page-description">
          Create and manage admin users for access to the Synlab backend.
        </p>

        {/* ----- USERS TABLE ----- */}
        <div className="admin-table-wrapper">
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Username</th>
              </tr>
            </thead>

            <tbody>
              {users.map((u) => (
                <tr
                  key={u.id}
                  className="admin-table-row"
                  onClick={() => handleRowClick(u)}
                >
                  <td>{u.id}</td>
                  <td>{u.username}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

      </div>

      {/* ----- FOOTER ----- */}
      <SynlabAdminFooter />
    </div>
  );
}

export default AdminUsers;
